import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress,
  TablePagination,
  Alert,
  Tooltip,
  Link,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from 'axios';

const RECOMMENDATION_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'Buy', icon: <TrendingUpIcon sx={{ fontSize: 14 }} />, color: '#4caf50', bg: 'rgba(76,175,80,0.12)' },
  { value: 'SELL', label: 'Sell', icon: <TrendingDownIcon sx={{ fontSize: 14 }} />, color: '#f44336', bg: 'rgba(244,67,54,0.12)' },
  { value: 'HOLD', label: 'Hold', icon: <PauseCircleIcon sx={{ fontSize: 14 }} />, color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
];

const TIME_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: '1', label: '1D' },
  { value: '3', label: '3D' },
  { value: '7', label: '1W' },
  { value: '30', label: '1M' },
  { value: 'custom', label: 'Custom Range' },
];

function HistoryDashboard({ onViewReport }) {
  const [analyses, setAnalyses] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTicker, setSearchTicker] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [timeRange, setTimeRange] = useState('');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
  const [watchlistDialogTicker, setWatchlistDialogTicker] = useState('');
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlists, setSelectedWatchlists] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [publicLinkAnchor, setPublicLinkAnchor] = useState(null);
  const [publicLinkUrl, setPublicLinkUrl] = useState('');

  const fetchPendingJobs = useCallback(async () => {
    try {
      const response = await axios.get('/api/queue/pending');
      if (response.data.success) {
        setPendingJobs(response.data.jobs || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending jobs:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchTicker) params.append('ticker', searchTicker);
      if (recommendation) params.append('recommendation', recommendation);
      if (timeRange === 'custom') {
        if (customDateFrom) params.append('from', customDateFrom);
        if (customDateTo) params.append('to', customDateTo);
      } else if (timeRange && timeRange !== 'custom') {
        params.append('days', timeRange);
      }

      const response = await axios.get(`/api/history?${params.toString()}`);
      if (response.data.success) {
        const allData = response.data.data;
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        setAnalyses(allData.slice(start, end));
        setTotal(response.data.total);
      } else {
        setError(response.data.error || 'Failed to load history');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [searchTicker, recommendation, timeRange, customDateFrom, customDateTo, page, rowsPerPage]);

  useEffect(() => {
    fetchHistory();
    fetchPendingJobs();
    const interval = setInterval(fetchPendingJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchHistory, fetchPendingJobs]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;
    
    try {
      const response = await axios.delete(`/api/history/${id}`);
      if (response.data.success) {
        fetchHistory();
      } else {
        setError(response.data.error || 'Failed to delete');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditNote = (id, currentNote) => {
    setEditingNoteId(id);
    setEditingNoteText(currentNote || '');
  };

  const handleSaveNote = async (id) => {
    try {
      await axios.put(`/api/history/${id}/note`, { note: editingNoteText });
      setAnalyses(prev => prev.map(a => 
        a.id === id ? { ...a, note: editingNoteText } : a
      ));
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save note');
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const getPublicReportUrl = (jobId) => {
    if (!jobId) return null;
    return `${window.location.origin}/reports/${jobId}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handlePublicLinkClick = (event, url) => {
    event.preventDefault();
    event.stopPropagation();
    setPublicLinkAnchor(event.currentTarget);
    setPublicLinkUrl(url);
  };

  const handleClosePublicLinkMenu = () => {
    setPublicLinkAnchor(null);
    setPublicLinkUrl('');
  };

  const getDecisionChip = (decision) => {
    if (!decision) return null;
    const colorMap = {
      BUY: { color: 'success' },
      SELL: { color: 'error' },
      HOLD: { color: 'warning' },
      OUTPERFORM: { sx: { bgcolor: '#81c784', color: '#1b5e20' } },
      UNDERPERFORM: { sx: { bgcolor: '#ef9a9a', color: '#b71c1c' } },
    };
    const config = colorMap[decision] || { color: 'default' };
    return (
      <Chip
        label={decision}
        color={config.color}
        size="small"
        sx={{ fontWeight: 'bold', ...config.sx }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRecommendationChange = (_, value) => {
    setRecommendation(value ?? '');
    setPage(0);
  };

  const handleTimeRangeChange = (event, value) => {
    if (value === 'custom') {
      setDatePickerAnchor(event.currentTarget);
    } else {
      setTimeRange(value ?? '');
      setPage(0);
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleApplyDateRange = () => {
    if (customDateFrom && customDateTo) {
      setTimeRange('custom');
      setPage(0);
      setDatePickerAnchor(null);
    }
  };

  const handleOpenWatchlistDialog = async (ticker) => {
    setWatchlistDialogTicker(ticker);
    setWatchlistLoading(true);
    setSelectedWatchlists([]);
    try {
      const response = await axios.get('/api/watchlists');
      if (response.data.success) {
        setWatchlists(response.data.data || []);
        setWatchlistDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Failed to load watchlists', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Error loading watchlists', severity: 'error' });
    } finally {
      setWatchlistLoading(false);
    }
  };

  const handleWatchlistToggle = (watchlistId) => {
    setSelectedWatchlists(prev =>
      prev.includes(watchlistId)
        ? prev.filter(id => id !== watchlistId)
        : [...prev, watchlistId]
    );
  };

  const handleAddToWatchlists = async () => {
    if (selectedWatchlists.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one watchlist', severity: 'warning' });
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const watchlistId of selectedWatchlists) {
        try {
          await axios.post(`/api/watchlists/${watchlistId}/items`, {
            ticker: watchlistDialogTicker,
          });
          successCount++;
        } catch (err) {
          if (err.response?.status !== 409) {
            errorCount++;
          }
        }
      }

      const message = errorCount > 0
        ? `Added to ${successCount} watchlist${successCount !== 1 ? 's' : ''}. ${errorCount} failed or already existed.`
        : `Added ${watchlistDialogTicker} to ${successCount} watchlist${successCount !== 1 ? 's' : ''}.`;

      setSnackbar({ open: true, message, severity: errorCount > 0 ? 'warning' : 'success' });
      setWatchlistDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: 'Error adding to watchlists', severity: 'error' });
    }
  };

  const handleCloseWatchlistDialog = () => {
    setWatchlistDialogOpen(false);
    setSelectedWatchlists([]);
    setWatchlistDialogTicker('');
  };

  const toggleButtonSx = {
    borderRadius: '20px !important',
    px: 1.5,
    py: 0.5,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: 0.3,
    border: '1px solid rgba(255,255,255,0.1) !important',
    color: 'text.secondary',
    textTransform: 'none',
    '&.Mui-selected': {
      color: 'primary.main',
      backgroundColor: 'rgba(0,255,0,0.08)',
      border: '1px solid rgba(0,255,0,0.35) !important',
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
  };

  const recToggleSx = (opt) => ({
    ...toggleButtonSx,
    '&.Mui-selected': {
      color: opt.color || 'primary.main',
      backgroundColor: opt.bg || 'rgba(0,255,0,0.08)',
      border: `1px solid ${opt.color || 'rgba(0,255,0,0.35)'}40 !important`,
    },
  });

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        mb={2}
      >
        <Typography variant="h5" color="primary" sx={{ mr: 1 }}>
          Analysis History
        </Typography>

        <TextField
          size="small"
          placeholder="Search by ticker..."
          value={searchTicker}
          onChange={(e) => {
            setSearchTicker(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              '& fieldset': { borderColor: 'primary.main' },
            },
          }}
        />
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        px={1.5}
        py={0.5}
        mb={2}
        borderRadius={3}
        border="1px solid rgba(255,255,255,0.08)"
        sx={{ backgroundColor: 'background.paper', width: 'fit-content' }}
      >
        <Typography variant="caption" sx={{ color: 'text.disabled', mr: 0.5, fontWeight: 600, fontSize: '0.68rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Signal
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={recommendation}
          onChange={handleRecommendationChange}
          size="small"
          sx={{ gap: 0.5, '& .MuiToggleButtonGroup-grouped': { mx: 0 } }}
        >
          {RECOMMENDATION_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} sx={recToggleSx(opt)}>
              {opt.icon && <Box component="span" sx={{ mr: 0.4, display: 'flex', alignItems: 'center' }}>{opt.icon}</Box>}
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        px={1.5}
        py={0.5}
        mb={3}
        borderRadius={3}
        border="1px solid rgba(255,255,255,0.08)"
        sx={{ backgroundColor: 'background.paper', width: 'fit-content' }}
      >
        <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: 'text.disabled', mr: 0.5, fontWeight: 600, fontSize: '0.68rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Period
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={timeRange}
          onChange={handleTimeRangeChange}
          size="small"
          sx={{ gap: 0.5, '& .MuiToggleButtonGroup-grouped': { mx: 0 } }}
          >
            {TIME_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value} sx={toggleButtonSx}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
      </Box>

      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, backgroundColor: 'background.paper', minWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Select Date Range
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              type="date"
              label="From"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: customDateTo || undefined }}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'primary.main' },
                },
              }}
            />
            <TextField
              type="date"
              label="To"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: customDateFrom || undefined }}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'primary.main' },
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleApplyDateRange}
                disabled={!customDateFrom || !customDateTo}
                sx={{ color: 'primary.main', flex: 1 }}
              >
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDatePickerClose}
                sx={{ color: 'text.secondary', flex: 1 }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Popover>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {pendingJobs.length > 0 && (
        <Paper sx={{ mb: 3, p: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'warning.main' }}>
          <Typography variant="h6" color="warning.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="warning" />
            Ongoing Tasks ({pendingJobs.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'warning.main', fontWeight: 'bold' }}>Ticker</TableCell>
                <TableCell sx={{ color: 'warning.main', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'warning.main', fontWeight: 'bold' }}>Started At</TableCell>
                <TableCell sx={{ color: 'warning.main', fontWeight: 'bold' }}>Job ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                      {job.ticker}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={job.status === 'processing' ? 'warning' : 'default'}
                      size="small"
                      icon={<CircularProgress size={12} color="inherit" />}
                    />
                  </TableCell>
                  <TableCell>{formatDate(job.started_at || job.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {job.id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : analyses.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="textSecondary">
              No analyses found. Run your first analysis to see it here.
            </Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Ticker</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Analysis Date</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Decision</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Note</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Public Link</TableCell>
                  <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyses.map((analysis) => (
                  <TableRow
                    key={analysis.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 255, 0, 0.05)',
                      },
                    }}
                    onClick={() => onViewReport(analysis.id)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Typography
                        variant="body1"
                        component={RouterLink}
                        to={`/stock/${analysis.ticker}`}
                        sx={{ fontWeight: 'bold', color: 'secondary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {analysis.ticker}
                      </Typography>
                    </TableCell>
                    <TableCell>{analysis.analysis_date || '-'}</TableCell>
                    <TableCell>{getDecisionChip(analysis.final_decision)}</TableCell>
                    <TableCell>
                      <Chip
                        label={analysis.status}
                        color={analysis.status === 'completed' ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {editingNoteId === analysis.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            placeholder="Add note..."
                            sx={{ width: 150 }}
                            autoFocus
                          />
                          <IconButton size="small" color="primary" onClick={() => handleSaveNote(analysis.id)}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelEditNote}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {analysis.note || '-'}
                          </Typography>
                          <IconButton size="small" onClick={() => handleEditNote(analysis.id, analysis.note)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {analysis.job_id ? (
                        <Tooltip title="Open public link">
                          <Link
                            href={getPublicReportUrl(analysis.job_id)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            onClick={(e) => handlePublicLinkClick(e, getPublicReportUrl(analysis.job_id))}
                          >
                            <LinkIcon fontSize="small" />
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {analysis.job_id.substring(0, 8)}...
                            </Typography>
                          </Link>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewReport(analysis.id);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <Tooltip title="Add to Watchlist">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenWatchlistDialog(analysis.ticker);
                          }}
                        >
                          <BookmarkIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(analysis.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 50, 100, 500]}
              sx={{
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: 'text.secondary',
                },
              }}
            />
          </>
        )}
      </TableContainer>

      <Menu
        anchorEl={publicLinkAnchor}
        open={Boolean(publicLinkAnchor)}
        onClose={handleClosePublicLinkMenu}
      >
        <MenuItem onClick={() => {
          copyToClipboard(publicLinkUrl);
          handleClosePublicLinkMenu();
        }}>
          Copy link
        </MenuItem>
        <MenuItem onClick={() => {
          window.open(publicLinkUrl, '_blank', 'noopener,noreferrer');
          handleClosePublicLinkMenu();
        }}>
          Open in new tab
        </MenuItem>
      </Menu>

      <Dialog open={watchlistDialogOpen} onClose={handleCloseWatchlistDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add {watchlistDialogTicker} to Watchlist
        </DialogTitle>
        <DialogContent>
          {watchlistLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : watchlists.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 2 }}>
              No watchlists found. Create one first to add this ticker.
            </Typography>
          ) : (
            <List sx={{ mt: 1 }}>
              {watchlists.map((watchlist) => (
                <ListItem
                  key={watchlist.id}
                  onClick={() => handleWatchlistToggle(watchlist.id)}
                  dense
                  button
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(0,255,0,0.05)' },
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedWatchlists.includes(watchlist.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={watchlist.name}
                    secondary={`${watchlist.item_count || 0} item${watchlist.item_count !== 1 ? 's' : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWatchlistDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddToWatchlists}
            color="primary"
            variant="contained"
            disabled={watchlistLoading || selectedWatchlists.length === 0 || watchlists.length === 0}
          >
            Add to Selected
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default HistoryDashboard;
